import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tokenAdminService, AdminTokenStats, AdminTokenPackage, AdminAIService } from '../../services/token-admin.service';

export function TokensAdminDashboard() {
  const [stats, setStats] = useState<AdminTokenStats | null>(null);
  const [packages, setPackages] = useState<AdminTokenPackage[]>([]);
  const [services, setServices] = useState<AdminAIService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [statsData, packagesData, servicesData] = await Promise.all([
        tokenAdminService.getStats(),
        tokenAdminService.getPackages(),
        tokenAdminService.getAIServices(),
      ]);
      setStats(statsData);
      setPackages(packagesData);
      setServices(servicesData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos de tokens');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando dashboard de tokens...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <span className="error-icon">❌</span>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Administración de Tokens</h1>
          <p className="text-gray-600">Monitorea y gestiona el sistema de tokens y servicios de IA</p>
        </div>
        <button onClick={loadData} className="btn btn-secondary">
          🔄 Actualizar
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-icon">💎</span>
            <span className="stat-value">{stats.totalTokensSold.toLocaleString()}</span>
            <span className="stat-label">Tokens Vendidos</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📊</span>
            <span className="stat-value">{stats.totalTokensUsed.toLocaleString()}</span>
            <span className="stat-label">Tokens Usados</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💰</span>
            <span className="stat-value">{formatCurrency(stats.totalRevenue)}</span>
            <span className="stat-label">Ingresos por Tokens</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">👥</span>
            <span className="stat-value">{stats.activeUsers}</span>
            <span className="stat-label">Usuarios Activos</span>
          </div>
        </div>
      )}

      {/* Dashboard Grid */}
      <div className="dashboard-row">
        {/* Paquetes de Tokens */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3>📦 Paquetes de Tokens</h3>
            <Link to="/admin/tokens/packages" className="text-primary hover:underline text-sm">
              Gestionar →
            </Link>
          </div>
          <div className="space-y-3">
            {packages.slice(0, 3).map((pkg) => (
              <div key={pkg.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{pkg.name}</p>
                  <p className="text-sm text-gray-500">{pkg.tokens} tokens</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">{formatCurrency(pkg.price)}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    pkg.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {pkg.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Servicios de IA */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3>🤖 Servicios de IA</h3>
            <Link to="/admin/tokens/ai-services" className="text-primary hover:underline text-sm">
              Gestionar →
            </Link>
          </div>
          <div className="space-y-3">
            {services.slice(0, 4).map((service) => (
              <div key={service.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{service.name}</p>
                  <p className="text-sm text-gray-500">{service.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">{service.cost} tokens</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    service.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {service.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Transacciones Recientes */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3>📊 Transacciones Recientes</h3>
          <Link to="/admin/tokens/transactions" className="text-primary hover:underline text-sm">
            Ver todas →
          </Link>
        </div>
        <RecentTransactionsTable />
      </div>
    </div>
  );
}

// Componente para mostrar transacciones recientes
function RecentTransactionsTable() {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    tokenAdminService.getTransactions({ limit: 5 })
      .then(data => setTransactions(data))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  const getTypeBadge = (type: string) => {
    const badges: Record<string, { class: string; label: string }> = {
      purchase: { class: 'bg-blue-100 text-blue-700', label: 'Compra' },
      usage: { class: 'bg-yellow-100 text-yellow-700', label: 'Uso' },
      refund: { class: 'bg-red-100 text-red-700', label: 'Reembolso' },
      bonus: { class: 'bg-green-100 text-green-700', label: 'Bonus' },
    };
    return badges[type] || { class: 'bg-gray-100 text-gray-700', label: type };
  };

  if (isLoading) return <div className="text-center py-4 text-gray-500">Cargando transacciones...</div>;
  if (transactions.length === 0) return <div className="text-center py-4 text-gray-500">No hay transacciones recientes</div>;

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Tipo</th>
            <th>Monto</th>
            <th>Descripción</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => {
            const badge = getTypeBadge(tx.type);
            return (
              <tr key={tx.id}>
                <td>
                  <div>
                    <p className="font-medium">{tx.userName || 'Usuario'}</p>
                    <p className="text-xs text-gray-500">{tx.userEmail}</p>
                  </div>
                </td>
                <td>
                  <span className={`text-xs px-2 py-1 rounded-full ${badge.class}`}>{badge.label}</span>
                </td>
                <td className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount} tokens
                </td>
                <td className="max-w-xs truncate">{tx.description}</td>
                <td>{new Date(tx.createdAt).toLocaleDateString('es-MX')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}